import { makeAutoObservable, runInAction } from "mobx";
import { matchPath } from 'react-router-dom';
import IframeService from "../services/iframe-service/iframe-service";
import { rootContainer, TYPE } from "../services/container/root";
import {  PERMISSION } from "../models/PermissionsTypes";
import { APP_PATHS } from "../constants/app-paths";
import { CapabilityTypeMap, IAnalyticsTabsCapability, IThemeCapability, PROFILE_CAPABILITIES } from "../models/ProfileCapabilities";
import MenuItemsStore from "./menu-items-store";
import * as Sentry from '@sentry/react';

interface IDashboardSettings {
    /* This is to avoid using `CurrentPortalStore` */
    portal_id: number;
    show_header_and_sidebar: boolean;
    layout_type: DashboardLayoutType;
    scope_change_url: string | null;
    accept_new_plan_url: string | null;
    is_dashboard_locked: boolean | null;
    is_hubspot_chat_shown: boolean | null;
    is_coverage_page_shown: boolean | null;
    is_sbr_promotion_page_shown: boolean | null;
    is_customization_supported: boolean | null;
    is_super_admin: boolean | null;
    is_super_admin_dashboard: boolean | null;
    user_permissions: number[] | null;
    theme_name: IThemeCapability;
    capabilities: Array<{ capability: PROFILE_CAPABILITIES; value: CapabilityTypeMap[PROFILE_CAPABILITIES] }>;
}

export enum DashboardLayoutType {
    ADMIN = 'admin',
    MERCHANT = 'merchant'
}

export enum ThemeName {
    GLOBAL_E = 'global-e',
    DEFAULT = 'default',
}

class DashboardSettingsStore {
    private _settings: IDashboardSettings = {
        portal_id: 0,
        show_header_and_sidebar: false,
        layout_type: DashboardLayoutType.MERCHANT,
        scope_change_url: null,
        accept_new_plan_url: null,
        is_dashboard_locked: null,
        is_hubspot_chat_shown: null,
        is_coverage_page_shown: null,
        is_sbr_promotion_page_shown: null,
        is_customization_supported: null,
        is_super_admin: null,
        is_super_admin_dashboard: null,
        user_permissions: [],
        capabilities: [],
        theme_name: IThemeCapability.DEFAULT,
    };
    private _isOnboardingWizardShown = false;
    private _areSettingsLoaded = false;
    private _capabilitiesMap: Map<PROFILE_CAPABILITIES, CapabilityTypeMap[PROFILE_CAPABILITIES]> = new Map();
    private _menuItemsStore: MenuItemsStore;

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
        this._menuItemsStore = new MenuItemsStore();
    }

    private get _iframeService(): IframeService { return rootContainer.get<IframeService>(TYPE.IframService); }

    get portalId(): number {
        return this._settings.portal_id;
    }

    get showHeaderAndSidebar(): boolean {
        return !this._isOnboardingWizardShown && this._settings.show_header_and_sidebar;
    }

    get layoutType(): DashboardLayoutType {
        return this._settings.layout_type;
    }

    get scopeChangeUrl(): string | null {
        return this._settings.scope_change_url;
    }

    get acceptNewPlanUrl(): string | null {
        return this._settings.accept_new_plan_url;
    }

    get isDashboardLocked(): boolean {
        return this._settings.is_dashboard_locked === true;
    }

    get isHubspotChatShown(): boolean {
        return this._settings.is_hubspot_chat_shown === true;
    }

    get isCoveragePageShown(): boolean {
        return this._settings.is_coverage_page_shown === true;
    }

    get isSBRPromotionPageShown(): boolean {
        return this._settings.is_sbr_promotion_page_shown === true;
    }

    get isCustomizationSupported(): boolean {
        return this._settings.is_customization_supported === true;
    }

    get isAnalyticsOrdersSupported(): boolean {
        const capability = this.resolveCapability(PROFILE_CAPABILITIES.ANALYTICS_TABS);
        return capability?.isOrdersSupported !== false;
    }

    get isAnalyticsCreditsSupported(): boolean {
        const capability = this.resolveCapability(PROFILE_CAPABILITIES.ANALYTICS_TABS);
        return capability?.isCreditsSupported !== false;
    }

    get isSuperAdmin(): boolean {
        return this._settings.is_super_admin === true;
    }

    get isSuperAdminDashboard(): boolean {
        return this._settings.is_super_admin_dashboard === true;
    }

    get userPermissions(): number[] {
        return this._settings.user_permissions ?? [];
    }

    get areSettingsLoaded(): boolean {
        return this._areSettingsLoaded;
    }

    get themeName(): IThemeCapability {
		// Theme must not be resolved via resolveCapability,
	    // because it's used in ThemeProvider, which is invoked before this store is initialized in AppProvider.
	    return this._settings.theme_name;
    }

    get menuItemsStore(): MenuItemsStore {
        return this._menuItemsStore;
    }

    public hasPermission(permission: PERMISSION): boolean {
        return this.userPermissions.includes(permission) || this.userPermissions.includes(PERMISSION.ALL);
    }

    public isPathAvailable(path: string): boolean {
        // Allow these pages if the dashboard is locked
        if (this.isDashboardLocked && !(
            path === APP_PATHS.DASHBOARD ||
            path === APP_PATHS.ADMIN_TROUBLESHOOTING ||
            this._startsWithBasePath(APP_PATHS.RETURN_COVERAGE, path) ||
            this._startsWithBasePath(APP_PATHS.SUBSCRIPTION_PLANS, path)
        )) {
            return false;
        }

        // Exact path matches
        if (path === APP_PATHS.DASHBOARD) {
            return this.hasPermission(PERMISSION.VIEW_HOME_PAGE);
        }

        if (
            path === APP_PATHS.CREATE_RMA ||
            this._startsWithBasePath(APP_PATHS.RETURNS, path) ||
            this._startsWithBasePath(APP_PATHS.RETURNS_NEW, path)
        ) {
            return this.hasPermission(PERMISSION.READ_RMA);
        }

        if (this._startsWithBasePath(APP_PATHS.RETURN_COVERAGE, path)) {
            return this.isCoveragePageShown;
        }

        if (path === APP_PATHS.SHIP_BY_RETURNGO) {
            return this.isSBRPromotionPageShown;
        }

        if (this._startsWithBasePath(APP_PATHS.SETTINGS_PORTAL_CONTENT, path)) {
            return this.isCustomizationSupported && this.hasPermission(PERMISSION.READ_SETTINGS);
        }

        if (this._startsWithBasePath(APP_PATHS.SETTINGS_BASE, path) ||
            this._startsWithBasePath(APP_PATHS.RETURN_REASONS, path) ||
            this._startsWithBasePath(APP_PATHS.RESOLUTIONS, path) ||
            this._startsWithBasePath(APP_PATHS.RETURN_METHODS, path) ||
            this._startsWithBasePath(APP_PATHS.POLICY_RULES, path) ||
            this._startsWithBasePath(APP_PATHS.EMAIL_BASE, path) ||
            this._startsWithBasePath(APP_PATHS.INTEGRATIONS_BASE, path) ||
            this._startsWithBasePath(APP_PATHS.LOOK_AND_FEEL, path)
        ) {
            return this.hasPermission(PERMISSION.READ_SETTINGS);
        }

        if (this._startsWithBasePath(APP_PATHS.ANALYTICS_RETURNS, path) ||
            this._startsWithBasePath(APP_PATHS.ANALYTICS_SESSIONS, path) ||
            this._startsWithBasePath(APP_PATHS.ANALYTICS_TRANSACTIONS, path) ||
            this._startsWithBasePath(APP_PATHS.ANALYTICS_SHIPMENTS, path)) {
            return this.hasPermission(PERMISSION.VIEW_ANALYTICS_PAGE);
        }

        if (this._startsWithBasePath(APP_PATHS.ANALYTICS_CREDITS, path)) {
            return this.isAnalyticsCreditsSupported && this.hasPermission(PERMISSION.VIEW_ANALYTICS_PAGE);
        }

        if (this._startsWithBasePath(APP_PATHS.ANALYTICS_ORDERS, path)) {
            return this.isAnalyticsOrdersSupported && this.hasPermission(PERMISSION.ALL);
        }

        if (path === APP_PATHS.BILLING) {
            const billingHistoryCapability = this.resolveCapability(PROFILE_CAPABILITIES.BILLING_HISTORY);
            return billingHistoryCapability.isBillingHistorySupported && this.hasPermission(PERMISSION.READ_BILLING_HISTORY);
        }

        if (path === APP_PATHS.SUBSCRIPTION_PLANS) {
            const subscriptionPlansCapability = this.resolveCapability(PROFILE_CAPABILITIES.SUBSCRIPTION_PLANS);
            return subscriptionPlansCapability.isSupported && this.hasPermission(PERMISSION.READ_MANAGE_PLAN);
        }

        if (path === APP_PATHS.STOREFRONT_LOCALIZATION) {
            const storefrontSettingsCapability = this.resolveCapability(PROFILE_CAPABILITIES.STOREFRONT_SETTINGS);
            return storefrontSettingsCapability.isSupported && this.hasPermission(PERMISSION.READ_SETTINGS);
        }

        if (path === APP_PATHS.ADMIN_ACCOUNTS) {
            return this.hasPermission(PERMISSION.READ_ACCOUNT);
        }

        if (this._startsWithBasePath(APP_PATHS.ADMIN_TROUBLESHOOTING, path)) {
            return this.isSuperAdmin || this.isSuperAdminDashboard;
        }

        if (this._startsWithBasePath(APP_PATHS.ADMIN_ACCOUNT_HEALTH, path)) {
            return this.isSuperAdmin || this.isSuperAdminDashboard;
        }

        return true;
    }

    public async load(): Promise<void> {
        try {
            const settings = await this._iframeService.getDashboardSettings();

            if (settings) {
                const parsedSettings = JSON.parse(settings as string);
                runInAction(() => {
                    this._settings = parsedSettings;

                    // Build capabilities map from capabilities array
                    if (parsedSettings.capabilities) {
                        this._capabilitiesMap.clear();
                        parsedSettings.capabilities.forEach((cap: IDashboardSettings['capabilities'][number]) => {
                            this._capabilitiesMap.set(cap.capability, cap.value);
                        });
                    }
                });
            }
        }
        catch (e) {
            console.error((e as Error).message);
        }
        finally {
            runInAction(() => {
                this._areSettingsLoaded = true;
            });
        }
    }

    public async openChat(): Promise<void> {
        await this._iframeService.openParentIframeChat();
    }

    set isOnboardingWizardShown(isOnboardingWizardShown: boolean) {
        this._isOnboardingWizardShown = isOnboardingWizardShown;
    }

    /**
     * Resolve a behavior profile capability with type safety
     *
     * @template T - The capability ID type
     * @param capabilityId - The capability identifier
     * @returns The resolved capability value, typed based on the capability ID, or undefined if not found
     *
     * @example
     * const theme = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.THEME);
     * if (theme === Theme.DEFAULT) { ... }
     *
     * @example
     * const productExchange = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.PRODUCT_EXCHANGE);
     * if (productExchange?.isFeatureEnabled && productExchange.isSupported) { ... }
     */
    public resolveCapability<T extends PROFILE_CAPABILITIES>(
        capabilityId: T
    ): CapabilityTypeMap[T] {
        const value = this._capabilitiesMap.get(capabilityId);

	    if (!value) {
		    // Edge case that must not happen: BE is out of sync with RMS and the required capability is missing.
		    // Fallback to an empty object to prevent runtime errors when accessing capability properties.
		    // Missing capabilities are treated as unsupported ("false wins").

		    // Capture the exception and alert via Sentry when a profile capability is not loaded.
		    Sentry.captureException(
			    new Error(`Profile capability not loaded: ${capabilityId}. Page refresh required.`)
		    );

		    return {} as CapabilityTypeMap[T];
	    }

        return value as CapabilityTypeMap[T];
    }

    private _startsWithBasePath(basePath: string, path: string): boolean {
        const match =  matchPath({ path: basePath, end: false }, path);
        return Boolean(match);
    }
}

const dashboardSettingsStore = new DashboardSettingsStore();
export default dashboardSettingsStore;
